import { Link } from 'react-router-dom'
import { IconeCheque, IconeSeta } from '../components/Icones'
import { TEXTOS } from '../lib/glossario'
import type { ItemDeNavegacao } from '../lib/navegacao'

/**
 * A tela de uma parte do sistema que ainda não existe.
 *
 * Não é um 404 enfeitado nem um "em breve" com foguete: quem chega aqui precisava trabalhar e
 * clicou num item do menu que o próprio sistema ofereceu. O mínimo devido é dizer o que a tela vai
 * fazer, em que fase ela entra e — quando a função existe no sistema anterior — onde fazer isso
 * hoje. O texto todo sai do registro de navegação, então a promessa da tela e o item do menu não
 * têm como divergir.
 *
 * @param props o item do registro correspondente a esta rota
 * @returns a página de "em construção"
 */
export function EmConstrucao({ item }: { item: ItemDeNavegacao }) {
  const Icone = item.icone

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header>
        <p className="text-xs font-semibold text-laranja-700">Em construção</p>
        <h1 className="titulo-da-pagina mt-1 flex items-center gap-2.5">
          <Icone className="h-7 w-7 text-niteroi-700" />
          {item.rotulo}
        </h1>
        <p className="mt-2 max-w-prose text-sm text-slate-600">{item.resumo}</p>
      </header>

      <section className="cartao p-5">
        <h2 className="text-base font-semibold text-slate-900">{TEXTOS.emConstrucao.titulo}</h2>
        <p className="mt-2 max-w-prose text-sm text-slate-600">{TEXTOS.emConstrucao.ajuda}</p>
        <p className="mt-3 text-sm font-medium text-slate-800">
          {item.fase
            ? `Está prevista para a ${item.fase} do plano de implantação.`
            : 'Ainda não tem fase definida no plano deste módulo, então também não tem data.'}
        </p>
      </section>

      {item.oQueVaiTer && item.oQueVaiTer.length > 0 && (
        <section className="cartao p-5">
          <h2 className="text-base font-semibold text-slate-900">O que esta tela vai fazer</h2>
          <ul className="mt-3 space-y-2.5">
            {item.oQueVaiTer.map((linha) => (
              <li key={linha} className="flex gap-2.5 text-sm text-slate-700">
                <IconeCheque className="mt-0.5 h-4 w-4 shrink-0 text-niteroi-600" />
                <span>{linha}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {item.noSistemaAnterior && <NoSistemaAnterior endereco={item.noSistemaAnterior} />}

      <p className="text-sm">
        <Link to="/painel" className="text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900">
          Voltar ao painel
        </Link>
      </p>
    </div>
  )
}

/**
 * Como fazer hoje, enquanto a tela daqui não chega.
 *
 * O aviso de que abre outro sistema não é formalidade: o sistema anterior tem login próprio, e
 * quem clicar sem saber disso vai achar que este aqui pediu a senha de novo. O texto "abre em
 * outra aba" fica também para o leitor de tela, que não vê o ícone da seta.
 *
 * @param props endereço da função equivalente no sistema anterior
 * @returns o cartão com o caminho de hoje
 */
function NoSistemaAnterior({ endereco }: { endereco: string }) {
  return (
    <section className="rounded-xl border border-laranja-200 bg-laranja-50 p-5">
      <h2 className="text-base font-semibold text-slate-900">Enquanto isso, use o sistema anterior</h2>
      <p className="mt-2 max-w-prose text-sm text-slate-700">
        Esta função continua no ar no sistema antigo, que segue funcionando normalmente. É outro
        sistema: abre em outra aba e pede o login de lá.
      </p>
      <a
        href={endereco}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-flex items-center gap-2 rounded-lg bg-niteroi-700 px-3.5 py-2 text-sm font-medium text-white hover:bg-niteroi-800"
      >
        Abrir no sistema anterior
        <span className="sr-only">(abre em outra aba)</span>
        <IconeSeta className="h-4 w-4 -rotate-45" />
      </a>
      <p className="mt-2 break-all text-xs text-slate-600">{endereco}</p>
    </section>
  )
}
